/**
 * Document repository (obj_type "K") — the wire contract and the transport seam.
 *
 * This package renders; it never decides where the API lives. The platform Run
 * view and the deployed standalone runtime serve the SAME endpoints under
 * different prefixes (`/api/dms/…` vs `/app/<name>/api/dms/…`), so each host
 * injects a `DmsClient` and the components below stay identical between them.
 * That is the whole reason these views live here rather than in `frontend/`:
 * one implementation, two surfaces, no drift.
 *
 * Every field a component reads is declared here, including `rights` — because
 * the UI must render from the rights the SERVER resolved, never from a guess
 * about what a role implies. A button hidden by the client is a courtesy; the
 * server denying the call is the control.
 */

/** The orders the server will sort a listing by. Mirrors `store.BROWSE_ORDERS`. */
export type DmsBrowseOrder = 'recent' | 'oldest' | 'name' | 'name_desc'

export const BROWSE_ORDER_LABELS: Record<DmsBrowseOrder, string> = {
  recent: 'Changed most recently',
  oldest: 'Changed longest ago',
  name: 'Name, A to Z',
  name_desc: 'Name, Z to A',
}

export interface DmsRights {
  view: boolean
  /** File a NEW document. Distinct from `edit`, which changes an existing one. */
  add: boolean
  edit: boolean
  download: boolean
  print: boolean
  /** Comment on a passage. Reviewing is not editing — see DocumentReview. */
  feedback: boolean
  /**
   * Apply a signature to a slot. Deliberately NOT implied by `edit`: an
   * approver signs a document they may not change, and an author changes one
   * they may not sign. Holding it is necessary but not sufficient — the server
   * also checks the caller is a legitimate approver of the level whose slot
   * they are filling.
   */
  sign: boolean
  share: boolean
  delete: boolean
  /**
   * Trigger a search re-index of the repository. Repository-level and costly
   * (re-extraction + re-embedding), so it is a named grant rather than a
   * side-effect of `edit`; super-roles hold it implicitly.
   */
  reindex: boolean
  reason?: string
}

// ── compound documents (a master and its ordered children) ────────────────

/** How an edge decides WHICH version of the child it means. */
export type DmsBindingMode = 'pinned' | 'latest' | 'latest_approved'

export interface DmsChild {
  edge_id: string
  child_document_id: string
  title: string
  label: string
  section_no: string
  order_no: number
  binding_mode: DmsBindingMode
  version_id: string
  version_label: string
  child_state: string
  /** A newer approved version exists than the one this edge is pinned to.
   *  Only ever true for a pinned edge — a tracking edge moves rather than
   *  going stale. */
  dirty: boolean
  newer_label: string
  /** The binding cannot be honoured at all: the pinned version is gone, or
   *  the child was deleted. */
  broken: boolean
  broken_reason: string
  /** A broken pin fell back to the child's current version, so the viewer can
   *  say "showing current instead". */
  fell_back: boolean
  required: boolean
  include_in_merge: boolean
}

export interface DmsBaseline {
  id: string
  status: string
  reason: string
  /** One value proving which exact child versions were approved together. */
  manifest_sha256: string
  item_count: number
  merge_status: string
  merge_page_count: number
  created_at?: string
}

export interface DmsCompound {
  enabled: boolean
  is_master: boolean
  is_child: boolean
  children: DmsChild[]
  /** How many children this reader may NOT see. A count only — naming them
   *  would confirm documents exist that the reader is not entitled to know
   *  about. */
  hidden_children: number
  content_status: string
  /** The status was computed over the VISIBLE subset. Say so, rather than
   *  letting a reader believe it describes the whole set. */
  content_status_partial: boolean
  snapshot: DmsBaseline | null
}

export interface DmsWhereUsedRow {
  edge_id: string
  parent_id: string
  title: string
  state: string
  binding_mode: DmsBindingMode
  section_no: string
  depth: number
  /** This master tracks the child and will follow a change to it, with
   *  nobody being asked. A pinned master is insulated. */
  affected: boolean
}

// ── signatures ────────────────────────────────────────────────────────────

/** One signature placeholder. Empty until it is filled — which is exactly
 *  what an unsigned `/Sig` field with no `/V` is inside the PDF. */
export interface DmsSignatureSlot {
  /**
   * Whether THIS reader may fill this slot right now — the server's own answer,
   * not a guess from the role name.
   *
   * It folds both rules `sign` will apply: nothing earlier is still
   * outstanding, and the reader holds the slot's role. Absent on a client that
   * has not been updated, which reads as "unknown" and leaves the old
   * behaviour: offer, and let the server refuse.
   */
  can_sign?: boolean
  slot_id: string
  slot_key: string
  field_name: string
  /** 21 CFR 11.50(a)(3): what this signature MEANS. */
  meaning: string
  role: string
  level_no: number
  ordinal: number
  required: boolean
  certify: boolean
  /**
   * Whether this slot wants a DRAWN mark as well as the credential evidence.
   *
   * A manifestation, never the authentication. Declared by the document TYPE,
   * so "which slots carry a drawn mark" is a property of the design and not of
   * whatever the client happened to send — a slot that asks for one and is
   * given none is refused rather than signed with an empty box.
   */
  ink?: boolean
  provider_kind: string
  status: string
  printed_name: string
  signed_at?: string
  reason: string
  /** 'none' means no cryptography was applied. The UI must not imply any. */
  pades_level: string
}

/**
 * One embedded cryptographic signature, as a PDF reader would see it.
 *
 * INTEGRITY, TRUST and COVERAGE are three separate fields on purpose. "These
 * bytes have not changed since this was signed" and "somebody we recognise
 * vouches for the signer" are different questions with different answers, and
 * a self-signed certificate is perfectly intact while being entirely
 * untrusted. Collapsing them into one boolean is how a screen ends up showing
 * a green tick over a document nobody can vouch for.
 */
export interface DmsEmbeddedSignature {
  field_name: string
  signed: boolean
  /** The bytes this signature covers are unchanged. */
  intact: boolean
  /** Intact AND whatever happened afterwards was permitted by its DocMDP. */
  valid: boolean
  /** Somebody in the configured trust roots vouches for the signer. */
  trusted: boolean
  /** e.g. 'ENTIRE_FILE'. An EARLIER signature in a multi-signature document
   *  covers its own revision, not the whole file — that is correct, and is
   *  why it is reported rather than read as a failure. */
  coverage: string
  covers_whole_document: boolean
  covers_own_revision?: boolean
  modification_level?: string
  signer: string
  reason?: string
  trust_reason?: string
}

export interface DmsSignatureVerification {
  /** False when this server has no signature library installed at all. */
  available: boolean
  /** False when the file would not parse — which is itself a detection: a
   *  tampered PDF often fails to READ rather than failing to verify. */
  readable: boolean
  count: number
  all_valid: boolean
  all_trusted?: boolean
  reason?: string
  signatures: DmsEmbeddedSignature[]
  version_id?: string
  signature_state?: string
}

/** A registered signing certificate, as the designer sees it. Never any key
 *  material: the blob is sealed in the platform store and the handle to it
 *  does not leave the server. */
export interface DmsSigningCredential {
  id: string
  label: string
  kind: string
  provider_kind: string
  subject_dn: string
  issuer_dn: string
  serial: string
  not_before?: string
  not_after?: string
  key_algorithm: string
  allowed_profiles: string[]
  active: boolean
  expired: boolean
}

export interface DmsSignatureEvent {
  id: string
  printed_name: string
  user_key: string
  on_behalf_of: string
  meaning: string
  signed_at?: string
  reason: string
  auth_method: string
  /** 21 CFR 11.200 wants at least two distinct identification components. */
  auth_components: number
  document_sha256: string
  pades_level: string
  provider_kind: string
}

export interface DmsSignatureStatus {
  enabled: boolean
  profile: string
  profile_label: string
  mode: string
  requires_pki: boolean
  request_id: string
  request_status: string
  slots: DmsSignatureSlot[]
  signed: number
  outstanding: number
  complete: boolean
  /** Whether any signature actually carries cryptography. When false the UI
   *  says "electronic signature (audit-trail record)" in words — a green tick
   *  over a database record is the one genuinely misleading thing this
   *  feature could show. */
  cryptographic: boolean
  /**
   * Fully signed, and the EXECUTED COPY was never filed.
   *
   * The copy is filed on the last signature; when that fails, the repository
   * holds a version whose signature lines all read "Not yet signed" under a
   * panel saying both parties signed. Both are true about different things,
   * and without this the reader is left to decide which one the document is.
   */
  executed_copy_pending?: boolean
  summary: string
  events: DmsSignatureEvent[]
}

export interface DmsDocumentRow {
  id: string
  title: string
  /** Set by `browse` in two batched queries. Deliberately no status here —
   *  that needs every child's state and would be a per-row fan-out. */
  is_master?: boolean
  child_count?: number
  is_child?: boolean
  doctype_id: string | null
  folder_id: string | null
  state: string
  current_version_id: string | null
  checked_out_by: string | null
  owner_user: string | null
  updated_at: string | null
  rights: DmsRights
}

export interface DmsVersion {
  id: string
  version_label: string
  version_major: number
  version_minor: number
  size_bytes: number
  content_type: string
  page_count: number
  effective_date: string | null
  /** `upload` | `checkin` | `authored` | `approval_promote` | `signed`.
   *  `signed` is the EXECUTED COPY — the version below it re-rendered with
   *  the collected signatures drawn into their lines. */
  source_kind: string
  /** The version an executed copy was made from. Absent on every other
   *  version, and on a repository whose owner never granted the additive
   *  column, so a reader of it must tolerate `undefined`. */
  signed_from_version_id?: string | null
  signature_state?: string | null
  ocr_status: string | null
  scan_status: string | null
  created_at: string | null
  created_by: string | null
  is_current?: boolean
  /**
   * Whether this version was WRITTEN in the document editor and so carries an
   * editable source. Derived from the stored version, not from the document
   * type's current mode: a type switched to `required` today does not make
   * yesterday's uploaded PDF openable in the editor.
   */
  is_editable?: boolean
  /** No source of its own, but an original the editor can be opened from
   *  (Word, OpenDocument, Pages, text). Weaker than `is_editable`: there is
   *  nothing stored to DIFF, so comparing versions still needs that one. */
  is_importable?: boolean
}

export interface DmsMetadataValue {
  field_key: string
  title: string
  data_type: string
  value: unknown
  source?: string | null
  confidence?: number | null
  confirmed_by?: string | null
  /** True when the field is a `secure` one. */
  secure?: boolean
  /** True when the caller is outside `reveal_roles` and got a mask. */
  masked?: boolean
  /** True for an IMAGE/FILE field — the bytes never travel in the list. */
  is_image?: boolean
  /** Whether a picture has been stored at all (shown even when masked). */
  has_image?: boolean
  /** Present only when this reader may SEE it; fetch the bytes separately. */
  image?: {
    field_key: string
    filename: string
    content_type: string
    size_bytes: number
  }
  error?: string
}

export interface DmsField {
  id: string
  field_key: string
  title: string
  data_type: string
  mandatory: unknown
  source: string
  secure_json?: unknown
  /** The type's declared default for this field — a constant, or a rule in
   *  the same vocabulary a transaction column's `default_value` uses
   *  (`auto_generate()`, `today()`, `:calendar_*`, `:audit_*`, a project
   *  variable, or a `{{...}}` token; see `resolveFieldDefault`). Absent on a
   *  repository whose tenant never got the additive column, which simply
   *  means no declared defaults. */
  default_json?: unknown
  /** The prefix parts of an `auto_generate_prefixed()` reference number. */
  series_json?: unknown
  /** SYSTEM-FILLED: the repository resolves this field's default on every
   *  write and the person filing is not offered a box for it. The server
   *  discards a posted value for it regardless of what a form drew. */
  protect?: unknown
  /** What the server resolved this field's default to, for THIS caller, right
   *  now. The only side that can — half the vocabulary is a query, a server
   *  clock or a directory lookup. Empty for a serial (see
   *  `generated_on_filing`) and for a type that declares no default. */
  default_preview?: string
  /** True when the value cannot exist until the document does — a serial,
   *  allocated once at the moment of filing. */
  generated_on_filing?: boolean

  // ── the transaction column's capture vocabulary ──
  //
  // A metadata field carries the same four things a transaction column does,
  // in the same shapes, because they are run by the same code: the widget by
  // the shared field control, the list format by the shared <DisplayCell>, the
  // rules by the engine's own evaluator and the on-change rule by its
  // item_change dispatcher. What crosses the wire is deliberately only what a
  // FORM needs — a validation's SQL, its business-function name and a Call-API
  // token stay on the server.

  /** The widget this field is captured with: a dropdown, radio group, checkbox
   *  group, single checkbox, text area, URL box or file upload. Absent means
   *  the control appropriate to `data_type`. */
  edit_mask?: DmsEditMask | null
  /** How the value is RENDERED in a list or a read-only panel — a status tag,
   *  a bar, a link, a flag, a rating. Display-only: it never changes what is
   *  captured or stored. The same `DisplayFormat` a transaction column carries,
   *  rendered by the same `<DisplayCell>`. */
  display?: Record<string, unknown> | null
  /** A STATIC picklist — the closed set a designer typed out. Distinct from a
   *  lookup: these are values, not rows. */
  values?: string[]
  /** `TABLE(DISPLAY, DATA)` when this field looks its values up from a master
   *  table. The filter and any raw SQL stay server-side; what the picker needs
   *  is the spec, the title and the data column. */
  lookup?: string
  has_lookup?: boolean
  lookup_title?: string
  lookup_sql_data_col?: string
  lookup_add_object?: string
  /** Whether changing this field runs a rule that can fill and lock others.
   *  A boolean, never the rule — the form only needs to know to ask. */
  has_item_change?: boolean
  /** Whether the field carries validation rules. Purely so a form can say the
   *  value will be checked; the check itself is always the server's. */
  has_validations?: boolean
}

/** The widget a metadata field is captured with — the transaction column's
 *  `edit_mask`, unchanged, so one control renders both. */
export interface DmsEditMask {
  mask: 'ddlb' | 'radio' | 'checkbox' | 'bool_check' | 'textbox'
    | 'textarea' | 'url' | 'image' | 'document' | ''
  values?: Array<{ data: string; display?: string }>
}

/** One row of a metadata field's reference picker. */
export interface DmsLookupRow {
  display: unknown
  data: unknown
  cells?: Record<string, unknown>
}

export interface DmsLookupResult {
  ok?: boolean
  rows: DmsLookupRow[]
  total?: number
  columns?: string[]
  display_col?: string
  data_col?: string
}

/** What an on-change rule did: values to set on OTHER fields, fields to lock
 *  or unlock, and a non-blocking message. Keyed by field key. */
export interface DmsItemChangeResult {
  updates?: Record<string, unknown>
  protect?: Record<string, 0 | 1>
  warning?: string | null
  error?: string
}

/** One failed (or merely advisory) validation, as the server reports it.
 *  `severity` is the transaction engine's: 'E' blocks, 'W' is acknowledgeable,
 *  'P' is informational. */
export interface DmsValidationFailure {
  field: string
  title?: string
  message: string
  code?: string | null
  severity?: 'E' | 'W' | 'P'
  rule?: string
}

/**
 * The per-field failures a refused save carried, or `[]` for any other error.
 *
 * The server sends `{detail: {error, message, failures}}` on a 422 precisely so
 * a form can point at the box the reader has to fix. Getting there means
 * reaching past whatever the HOST's transport wrapped it in, and there are
 * three of those: the platform client attaches `failures` to the Error it
 * throws, the runtime's `ApiError` keeps the envelope on `detail` (and mirrors
 * it under `response.data.error.details`), and a bare axios rejection still has
 * `response.data.detail`. Reading all three here is what lets the shared views
 * highlight a field without knowing which host they are mounted in.
 */
export function metadataFailures(error: unknown): DmsValidationFailure[] {
  const rows = (candidate: unknown): DmsValidationFailure[] | null => {
    const found = (candidate as { failures?: unknown })?.failures
    return Array.isArray(found) ? found as DmsValidationFailure[] : null
  }
  const err = error as {
    failures?: unknown
    detail?: unknown
    cause?: unknown
    response?: { data?: { detail?: unknown; error?: { details?: unknown } } }
  }
  const data = err?.response?.data
  const cause = (err?.cause as { response?: { data?: { detail?: unknown } } })
  return rows(err)
    ?? rows(err?.detail)
    ?? rows(data?.detail)
    ?? rows(data?.error?.details)
    ?? rows(cause?.response?.data?.detail)
    ?? []
}

/**
 * Whether documents of a type are written in the built-in editor rather than
 * uploaded. `off` is what every type that existed before authoring shipped
 * keeps; `required` also means nothing may be FILED against the type from
 * outside, which is the point for an SOP whose wording is the record.
 */
export interface DmsAuthoring {
  mode: 'off' | 'optional' | 'required'
  /** The formatting template key documents of this type are written against. */
  template: string
}

export interface DmsDocType {
  id: string
  type_key: string
  title: string
  source: 'design' | 'runtime' | string
  fields: DmsField[]
  search_flags: Record<string, boolean>
  ai_mode: string
  authoring?: DmsAuthoring
  /** What this type accepts. Empty = anything. Lets the panel refuse a file
   *  against the type the user just picked without a round trip. */
  file_rules?: DmsFileRules
  /** Set when the metadata lives in a transaction record rather than in the
   *  fields above — the panel must not draw a second, weaker form over it. */
  metadata_binding?: DmsMetadataBinding
}

/**
 * One comparison in a built filter.
 *
 * `field` is either a document property — `doc:title`, `doc:doctype`,
 * `doc:folder`, `doc:state`, `doc:owner`, `doc:created`, `doc:updated` — or a
 * metadata field as `meta:<field_key>`. The server knows the same names
 * (`store.BROWSE_DOC_FIELDS`); nothing else compiles, so an unknown field is
 * dropped rather than silently matching everything.
 *
 * The operator vocabulary is the transaction engine's, so somebody who has
 * built a filter on a transaction list already knows this one.
 */
export type DmsConditionOp =
  | 'eq' | 'ne' | 'contains' | 'not_contains' | 'starts' | 'ends'
  | 'in' | 'not_in' | 'between' | 'gt' | 'lt' | 'ge' | 'le'
  | 'empty' | 'not_empty'

export interface DmsCondition {
  field: string
  op: DmsConditionOp
  /** One value, or several for `in` / `not_in`. */
  value?: unknown
  /** `between` only. */
  from?: unknown
  to?: unknown
}

/** Conditions joined by one connective. Groups may nest (the server compiles
 *  four levels), which is what makes "(a or b) and c" expressible. */
export interface DmsConditionGroup {
  join: 'and' | 'or'
  conditions: Array<DmsCondition | DmsConditionGroup>
}

export function isConditionGroup(
  node: DmsCondition | DmsConditionGroup,
): node is DmsConditionGroup {
  return Array.isArray((node as DmsConditionGroup).conditions)
}

/** Everything the browse bar is doing — the whole state a saved filter keeps,
 *  so applying one restores the pills as well as the expression. */
export interface DmsFilterSpec {
  query?: string
  doctypeIds?: string[]
  states?: string[]
  owners?: string[]
  conditions?: DmsConditionGroup | null
}

export interface DmsSavedFilter {
  id: string
  name: string
  /** Offered to everyone in the repository, not just its author. */
  shared: boolean
  owner_user: string
  mine: boolean
  /** Whether THIS caller may rename, overwrite or delete it. */
  can_edit: boolean
  filter: DmsFilterSpec
}

export interface DmsSavedFilterList {
  filters: DmsSavedFilter[]
  /** Whether this caller may share a filter with everyone (administrators). */
  can_share: boolean
  /** False where the repository could not provision the saved-filter table —
   *  filters still apply for the visit, they just cannot be kept. */
  can_save: boolean
}

export interface DmsFolder {
  id: string
  parent_id: string | null
  name: string
  /** Materialised `/a/b/c` of folder IDS — the tree is built from this. */
  path: string
  /** Document type KEYS this folder accepts. Empty/absent = any. */
  folder_type?: unknown
  /**
   * Where it sits among its siblings — the model's definition order to begin
   * with, then wherever somebody dragged it. Absent on a repository that never
   * got the column, and `orderFolders` falls back to creation order there.
   */
  ordinal?: number | null
}

/** Sibling order, never alphabetical — see `orderFolders`. */
export function compareFolders(a: DmsFolder, b: DmsFolder): number {
  const placed = (f: DmsFolder) => (f.ordinal === null || f.ordinal === undefined
    ? Number.POSITIVE_INFINITY : Number(f.ordinal))
  const diff = placed(a) - placed(b)
  if (diff) return diff
  // Never-placed folders keep the order the server sent, which is creation
  // order — the order the library was built in, and the closest thing to a
  // designed sequence a repository that predates positions has.
  return 0
}

/**
 * Depth-first order with each folder's depth, derived from the id path.
 *
 * Shared by every folder list in the product — the rail, the move picker —
 * because a folder that is third in the sidebar and first in the "move to"
 * menu is two different libraries to the person reading them.
 */
export function orderFolders(
  folders: DmsFolder[],
): Array<{ folder: DmsFolder; depth: number }> {
  const children = new Map<string, DmsFolder[]>()
  for (const folder of folders) {
    const parent = String(folder.parent_id || '')
    const list = children.get(parent) || []
    list.push(folder)
    children.set(parent, list)
  }
  for (const list of children.values()) {
    // Stable, so equal-ordinal siblings keep the server's order.
    list.sort(compareFolders)
  }
  const out: Array<{ folder: DmsFolder; depth: number }> = []
  const walk = (parent: string, depth: number) => {
    // A depth cap, because a corrupt parent chain must not hang the browser.
    if (depth > 12) return
    for (const folder of children.get(parent) || []) {
      out.push({ folder, depth })
      walk(String(folder.id), depth + 1)
    }
  }
  walk('', 0)
  // Anything whose parent is missing (a folder the caller cannot see) still has
  // to appear, or its documents look unfiled.
  const seen = new Set(out.map((entry) => String(entry.folder.id)))
  for (const folder of folders) {
    if (!seen.has(String(folder.id))) out.push({ folder, depth: 0 })
  }
  return out
}

/** What a version's file actually IS. Derived from the stored bytes, never typed. */
export interface DmsFileInfo {
  filename?: string
  extension?: string
  content_type?: string
  type_label?: string
  size_bytes?: number
  size_label?: string
  page_count?: number
  sha256?: string
}

/** What a kind of document will ACCEPT. Empty = anything. */
export interface DmsFileRules {
  extensions?: string[]
  mime_types?: string[]
  min_size_kb?: number
  max_size_mb?: number
}

/** Set when the type's metadata lives in a transaction record. */
export interface DmsMetadataBinding {
  obj_name?: string
  preview_fields?: string[]
  txn_id?: string
}

/** One reviewer's comment on a passage of a version. */
export interface DmsReviewItem {
  id: string
  review_id: string
  ordinal: number
  page_no: number
  section_label: string | null
  anchor_text: string | null
  anchor_start: number
  anchor_end: number
  kind: 'rewrite' | 'comment' | string
  suggestion: string | null
  status: 'open' | 'accepted' | 'rejected' | 'applied' | string
  resolution: string | null
  created_at: string | null
}

/** One reviewer's submission — many comments, one version, one decision. */
export interface DmsReview {
  id: string
  document_id: string
  version_id: string
  user_key: string
  summary: string | null
  status: 'draft' | 'submitted' | 'in_approval' | 'applied' | 'rejected' | string
  approval_instance_id: string | null
  decided_by: string | null
  decision_note: string | null
  created_at: string | null
  items: DmsReviewItem[]
}

/** One staged file's pre-flight verdict — see `repository.preflight`. */
export interface DmsPreflight {
  filename: string
  size_bytes: number
  size_label?: string
  content_type?: string
  extension?: string
  type_label?: string
  kind?: string
  /** False when the name's extension disagrees with the actual bytes. */
  extension_agrees?: boolean
  /** Everything that would refuse this file. Empty = it would be accepted. */
  problems: string[]
  scan: {
    /** clean | blocked | skipped | error | unavailable */
    status: string
    detail: string
  }
}

/** What the upload panel needs to know before it sends anything. */
export interface DmsUploadPolicy {
  scan_configured: boolean
  /** True when the repository requires a scan — with no scanner, every
   *  document is blocked at ingest, which is worth saying up front. */
  scan_required: boolean
  max_check_bytes: number
}

export interface DmsAction {
  action: string
  from: string
  to: string
  label?: string
  via?: string
}

/**
 * One page of the library, plus what this caller may do to the library ITSELF.
 *
 * `rights` is repository-scope, not document-scope: `add` (file something new,
 * create a folder) and `reindex` have no document to hang off, so they are
 * answered here. The screen renders its repository-level controls from this
 * for the same reason every row renders its own from `row.rights` — the server
 * resolved them, and a control the caller will be refused should not be shown
 * in the first place.
 */
export interface DmsBrowseResult {
  rows: DmsDocumentRow[]
  total: number
  scope: string
  rights: DmsRights
}

export interface DmsDocumentDetail {
  document: Record<string, unknown> & { id: string; title: string }
  rights: DmsRights
  metadata: DmsMetadataValue[]
  versions: DmsVersion[]
  doctype: DmsDocType | null
  file_info: DmsFileInfo
  file_rules: DmsFileRules
  metadata_binding: DmsMetadataBinding
  search_flags: Record<string, boolean>
  ai_mode: string
  actions: DmsAction[]
  authoring?: DmsAuthoring
  /** The revision being written against a document that is already in force. */
  revision?: DmsRevision
  /** Present whenever the repository has compound support; inert when the
   *  feature is unavailable, so a host never has to branch on it. */
  compound?: DmsCompound
}

/**
 * A revision in progress on a released document.
 *
 * Derived by the server from the versions themselves — a version newer than
 * the one being served — rather than stored as a flag, so it can never
 * disagree with what is actually on the document. See
 * `repository._revision_block`.
 *
 * While this is `in_progress` the document keeps its state and keeps serving
 * `released_version_id`: a reader downloading it gets the approved text, not
 * the draft somebody is part way through.
 */
export interface DmsRevision {
  in_progress: boolean
  /** The version readers are being served — the one still in force. */
  released_version_id: string
  released_label?: string
  /** The newer version awaiting review, empty when nothing is pending. */
  candidate_version_id: string
  candidate_label: string
  /** Whether the candidate can be re-opened in the editor to carry on. */
  candidate_is_editable: boolean
  candidate_created_at?: string
  candidate_created_by?: string
}

export interface DmsCitation {
  n?: number
  document_id: string
  title: string
  version_id: string
  version: string
  effective_date: string | null
  page: number | null
  state: string
  link: string
  excerpt?: string
  score?: number
}

export interface DmsAnswer {
  answer: string
  citations: DmsCitation[]
  sources: number
  backend?: string
  error?: string
  mandatory_preserved?: boolean
}

export interface DmsOutstandingAck {
  assignment_id: string
  document_id: string
  title: string
  version_id: string
  version: string
  due_at: string | null
  overdue: boolean
}

// ── approvals ─────────────────────────────────────────────────────────────
//
// A document approval is NOT a transaction approval. It is staged in the same
// table and shows the same levels, but it is filed under the synthetic object
// name `DMS_<repo>` and promoting it means moving one version pointer, which
// only the repository knows how to do. That is why these are their own
// endpoints and their own queue rather than a branch in the transaction inbox.

export interface DmsPendingApproval {
  instance_id: string
  document_id: string
  title: string
  /** The state the document reaches if this is approved. */
  target_state: string
  /** The version being proposed. Empty for a review approval. */
  candidate_version_id: string
  pending_roles: string
  submitted_by: string
}

/**
 * The record a document is the FACE of, when a Document Form (`E`) filed it.
 *
 * Present only for such a document; an uploaded or hand-written one has no
 * source and is edited in the repository's own writer as it always was.
 */
export interface DmsDocumentSource {
  /** The `E` object that filed it. */
  form_obj: string
  /** The transaction the form records against — where the editor lives. */
  obj_name: string
  /** The record itself. */
  txn_id: string
  instance_id: string
  title: string
}

/**
 * One signature waiting on the reader, in one document.
 *
 * The NEXT slot only, and only where the reader may fill it — the same rule
 * the server will enforce when they try, applied a step earlier so the queue
 * cannot offer work that would then be refused.
 */
export interface DmsPendingSignature {
  request_id: string
  document_id: string
  title: string
  slot_id: string
  slot_key: string
  /** 21 CFR 11.50(a)(3): what signing this slot MEANS. */
  meaning: string
  /** The role the slot is for, or '' when anybody who may sign can fill it. */
  role: string
  signed: number
  total: number
  requested_at?: string | null
}

/** Where the current step stands against its workflow SLA. `has_sla` false
 *  means no deadline was configured — which is not the same as "on time", and
 *  must not be rendered as if it were. */
export interface DmsApprovalSla {
  has_sla: boolean
  sla_hours: number | null
  due_at: string | null
  overdue: boolean
  hours_over: number | null
  activity: string | null
}

export interface DmsApprovalDetail {
  /** `version` = a proposed release. `review` = a set of accepted review
   *  comments being signed off. There is no candidate version in the second,
   *  so a reviewer must not be shown an empty diff as though nothing changed. */
  kind: 'version' | 'review'
  document_id: string
  title: string
  target_state: string
  candidate_version_id: string
  review_id: string
  instance: Record<string, unknown>
  sla: DmsApprovalSla
}

// ── search re-indexing ────────────────────────────────────────────────────

/** Progress of a repository's re-index run — counts of its queued jobs. */
export interface DmsReindexStatus {
  active: boolean
  pending: number
  running: number
  done: number
  parked: number
  total: number
}

/**
 * What a re-index would actually change — the confirmation dialog's data.
 * `pending_count`/`pending_bytes` are the documents whose index is missing or
 * out of date; `reasons` says why (never indexed / embedding profile changed /
 * vectors missing). Policy-excluded documents are counted separately so the
 * dialog never promises work the pipeline would skip.
 */
export interface DmsReindexPending {
  documents: number
  indexed: number
  excluded: number
  pending_count: number
  pending_bytes: number
  total_bytes: number
  reasons: { not_indexed: number; profile_changed: number; missing_embeddings: number }
}

// ── compliance evidence ───────────────────────────────────────────────────

export interface DmsAckReportRow {
  assignment_id: string
  document_id: string
  title: string | null
  version_id: string
  /** `role:<name>` or `user:<key>`. */
  audience: string
  active: boolean
  due_at: string | null
  acknowledged_count: number
  population: string[]
  /** False when role membership could not be resolved. The server refuses to
   *  report a falsely complete list, and so must the UI: a percentage computed
   *  over an unresolved population reads as compliance nobody has evidence of. */
  population_resolved: boolean
  outstanding: string[]
  records: Array<{ user: string; at: string | null; ip?: string | null }>
}

/** Somebody a USER-typed metadata field can name. */
export interface DmsUser {
  user_key: string
  name: string
  email?: string
  /** False when the directory could not resolve the key — a departed owner is
   *  still a fact about the document, shown by id rather than hidden. */
  known?: boolean
}

export interface DmsAuditEntry {
  id: string
  repo_obj: string
  document_id: string | null
  version_id: string | null
  user_key: string | null
  ip: string | null
  action: string
  detail_json: unknown
  at: string | null
}

/**
 * The host-supplied transport. Every method maps to one endpoint; the host
 * supplies base URL, credentials and error normalisation.
 *
 * `pageUrl` returns a URL rather than bytes on purpose — a page image is put
 * straight into an `<img src>` so the browser streams and caches it per its own
 * `no-store` header, instead of the component holding megabytes of base64.
 */
export interface DmsClient {
  // ── compound documents (optional: a host that does not serve them simply
  //    omits these, and the panel hides itself with no branching) ──
  children?(parentId: string): Promise<DmsCompound>
  addChild?(parentId: string, childId: string,
            opts?: { binding_mode?: DmsBindingMode; label?: string;
                     section_no?: string; required?: boolean }): Promise<unknown>
  updateChild?(edgeId: string,
               values: Record<string, unknown>): Promise<unknown>
  removeChild?(edgeId: string): Promise<unknown>
  reorderChildren?(parentId: string, order: string[]): Promise<unknown>
  whereUsed?(documentId: string, recursive?: boolean):
    Promise<{ parents: DmsWhereUsedRow[]; hidden_parents: number }>
  impact?(documentId: string): Promise<Record<string, unknown>>
  baselines?(documentId: string): Promise<{ baselines: DmsBaseline[] }>
  freezeBaseline?(documentId: string): Promise<unknown>
  mergedUrl?(snapshotId: string): string

  // ── signatures (optional for the same reason) ──
  signatureStatus?(documentId: string): Promise<DmsSignatureStatus>
  openSignatureRequest?(documentId: string): Promise<{ request_id: string }>
  /**
   * Fill one slot.
   *
   * `auth_components` is GONE, deliberately (Phase 0.1). A count asserted by
   * the party being authenticated is not authentication: the shipped panel
   * sent `1`, so a Part 11 document could not be signed at all, and a caller
   * sending `2` got a Part 11 signature having proved nothing. The server now
   * derives it from the credential below, which it verifies itself.
   */
  sign?(requestId: string,
        opts?: { slot_id?: string; reason?: string;
                 password?: string; otp_code?: string;
                 /** A drawn mark as a `data:image/png;base64,...` URL, for a
                  *  slot whose `ink` says it wants one. */
                 ink?: string;
                 /** What the signer read, from the challenge. See
                  *  `DmsSignatureChallenge.content_token`. */
                 content_token?: string }):
    Promise<unknown>
  /**
   * Check the cryptographic signatures the FILE itself carries.
   *
   * Separate from `signatureStatus`, which reports the campaign this server
   * recorded. The two answer different questions and a reader needs both: the
   * campaign says who attested to what, and this says whether a third party
   * opening the file would agree.
   */
  verifySignatures?(documentId: string, versionId?: string):
    Promise<DmsSignatureVerification>
  /**
   * File the executed copies a completed campaign never got.
   *
   * An explicit act with its own call, deliberately: a status read that
   * quietly repaired things would hide the failure it is reporting.
   */
  repairExecutedCopies?(): Promise<{
    checked: number
    filed: { document_id: string; version_id: string }[]
    failed: { document_id: string; error: string }[]
  }>
  /**
   * What this slot will ask for, BEFORE the modal is drawn.
   *
   * So the credential box asks for the right thing, and so a regime this
   * installation cannot satisfy is reported while the person can still do
   * something about it — rather than after they have read the attestation and
   * typed their password.
   */
  signatureChallenge?(requestId: string, slotId?: string):
    Promise<DmsSignatureChallenge>
  declineSignature?(requestId: string, slotId: string,
                    reason?: string): Promise<unknown>
  signatureManifestUrl?(documentId: string): string
  /**
   * Documents in this repository waiting for THIS reader to sign.
   *
   * Optional for the reason the approval queue is: a host that does not serve
   * it simply shows no signature section, rather than the tab 404-ing. Returns
   * `[]` — not an error — for a reader who signs nothing.
   */
  pendingSignatures?(): Promise<DmsPendingSignature[]>
  /**
   * Which record filed this document, if a Document Form did.
   *
   * Optional: a host that does not serve it gets the ordinary editor for every
   * document, which is what shipped before — never a broken button.
   */
  documentSource?(documentId: string): Promise<DmsDocumentSource | null>

  browse(input: {
    query?: string
    doctypeId?: string
    folderId?: string
    states?: string[]
    /** Documents this person looks after. */
    owner?: string
    /** Multi-select pills. Values WITHIN one pill are "any of these"; the
     *  pills still narrow each other. Sent alongside the singular fields
     *  above, which stay for callers that only ever pick one. */
    doctypeIds?: string[]
    owners?: string[]
    /** A built expression, ANDed with the pills — see `DmsConditionGroup`. */
    conditions?: DmsConditionGroup | null
    /** A key of the server's whitelist: recent | oldest | name | name_desc.
     *  Sorting is done in the QUERY, never over the loaded page — a control
     *  that reorders 50 of 142 rows tells the reader something untrue. */
    order?: DmsBrowseOrder
    limit?: number
    offset?: number
  }): Promise<DmsBrowseResult>

  /** Saved filters for this caller (their own + the shared ones). Optional so
   *  a host that does not serve them simply offers no saved list, and the
   *  builder still works for the visit. */
  savedFilters?(): Promise<DmsSavedFilterList>
  saveFilter?(input: {
    name: string
    filter: DmsFilterSpec
    shared?: boolean
    /** Set to overwrite an existing one rather than create another. */
    filterId?: string
  }): Promise<{ filter_id: string }>
  deleteFilter?(filterId: string): Promise<unknown>

  detail(documentId: string): Promise<DmsDocumentDetail>
  types(): Promise<DmsDocType[]>
  folders(): Promise<DmsFolder[]>

  upload(input: {
    file: File
    title?: string
    doctypeId?: string
    folderId?: string
    documentId?: string
    major?: boolean
    /** Filed WITH the document. A controlled document with no metadata has no
     *  retention clock, no review schedule and nothing to search on, and a
     *  two-step flow leaves exactly that document behind when step two fails. */
    metadata?: Record<string, unknown>
  }): Promise<{
    document_id: string
    version_id: string
    /** Rules that did not refuse the upload but that somebody should read —
     *  severity 'W' and 'P'. There is no acknowledge round on this path (the
     *  panel posts a file and its details together), so this is where they
     *  are seen or nowhere. */
    advisories?: DmsValidationFailure[]
  }>

  /** Pre-flight. Optional so a host that does not serve it simply stages files
   *  without a verdict rather than breaking the panel. */
  checkUploads?(input: {
    files: File[]
    doctypeId?: string
    folderId?: string
  }): Promise<DmsPreflight[]>

  uploadPolicy?(): Promise<DmsUploadPolicy>

  /**
   * Save metadata. Rejects with the server's 422 envelope
   * (`{error:'VALIDATION_FAILED', failures:[…]}`) when a rule blocks;
   * `ignoreWarnings` re-sends with severity-'W' codes acknowledged, the same
   * "ignore warnings and proceed" loop a transaction save uses.
   *
   * Resolves with the ADVISORIES the save raised — severity 'W' and 'P' rules,
   * which do not block and so have no other way of being seen. The server has
   * always returned them; both clients used to drop the response on the floor,
   * which made a warning-severity rule indistinguishable from no rule at all.
   */
  saveMetadata(documentId: string, values: Record<string, unknown>,
               opts?: { ignoreWarnings?: string[] }): Promise<DmsValidationFailure[]>

  /**
   * Rows for a metadata field's reference picker.
   *
   * Optional: a host that does not serve it simply leaves such a field as a
   * text box, which is what it was before. Note what is NOT sent — the filter.
   * It is read off the stored field definition server-side, because a reader
   * who can post a WHERE fragment has a query tool.
   */
  metadataLookup?(input: {
    doctypeId: string
    fieldKey: string
    search?: string
    limit?: number
    /** The other values on the form, so a filter can narrow by them. */
    values?: Record<string, unknown>
  }): Promise<DmsLookupResult>

  /**
   * Run the on-change rule attached to a metadata field. Optional for the same
   * reason; a field with no rule returns empty rather than an error, so the
   * caller may ask whenever a value changes.
   */
  metadataItemChange?(input: {
    doctypeId: string
    fieldKey: string
    values: Record<string, unknown>
  }): Promise<DmsItemChangeResult>
  confirmMetadata(documentId: string, fields: string[]): Promise<void>
  act(documentId: string, action: string, comment?: string): Promise<{ staged?: boolean; target_state?: string }>
  checkOut(documentId: string): Promise<void>
  cancelCheckOut(documentId: string): Promise<void>

  /** A URL for one watermarked page image. */
  /**
   * Resolve a URL of this repository's into something the browser can show or
   * save, CARRYING this client's credentials.
   *
   * `pageUrl` and friends hand back a plain URL for an `<img src>` or an
   * `<a href>`, and a browser loading one of those sends cookies and nothing
   * else. A PROJECT USER — invited from the application's own administration
   * screen — holds no cookie at all: their session is an opaque bearer token
   * the client attaches as headers, which an `<img>` request cannot carry. So
   * every page image, thumbnail and download came back 404 "Project not found"
   * for exactly the people the running application is for, while every POST on
   * the same screen worked.
   *
   * Optional: a host whose only caller is a cookie-bearing platform account
   * omits it and the plain URL is used, exactly as before.
   */
  authedBlobUrl?(url: string): Promise<string>
  pageUrl(documentId: string, page: number, versionId?: string): string

  /**
   * A small watermarked image of page one, for the library's cards.
   *
   * Optional because a host that cannot render (no Pillow, no pdfium) simply
   * never offers card view. It is audited as `thumbnail`, NOT as `view_page`:
   * a compliance report counting who read a document must not be filled with a
   * row per card that scrolled past.
   */
  thumbnailUrl?(documentId: string, versionId?: string): string
  /** A URL that downloads the original file (only offered when `download`). */
  downloadUrl(documentId: string, versionId?: string): string
  /**
   * A URL for an image-valued metadata field. The server re-checks the field's
   * reveal roles on every fetch, so this being present in the DOM is not what
   * grants access — it only saves a round trip for a reader who has it.
   */
  metadataImageUrl?(documentId: string, fieldKey: string): string

  ask(question: string, opts?: { historical?: boolean; documentIds?: string[] }): Promise<DmsAnswer>
  search(question: string): Promise<{ hits: DmsCitation[]; backend: string }>
  compare(documentId: string, a: string, b: string): Promise<{ summary: string; diff: string[] }>

  // ── approvals (optional: a downloaded runtime has no approval engine, so it
  //    omits these and the queue hides itself rather than 404-ing) ──
  /** Approvals waiting on THIS caller. Returns `[]` — not an error — for a
   *  reader who approves nothing, so the queue can decide to stay hidden. */
  pendingApprovals?(): Promise<DmsPendingApproval[]>
  approvalDetail?(instanceId: string): Promise<DmsApprovalDetail>
  decideApproval?(input: {
    instanceId: string
    decision: 'approve' | 'reject'
    /** Required by the queue on a rejection: a rejection with no reason
     *  returns the document to its owner with nothing to act on. */
    comment?: string
    activityId?: string
  }): Promise<{
    status: 'pending' | 'approved' | 'rejected'
    message?: string
    dms?: { reverted: boolean; error?: boolean }
  }>

  // ── compliance evidence (optional; administrator-only on the server) ──
  ackReport?(documentId?: string): Promise<DmsAckReportRow[]>
  auditLog?(input?: { documentId?: string; limit?: number }): Promise<DmsAuditEntry[]>

  // ── search re-indexing (optional; gated by the `reindex` right on the
  //    server — the refused status probe is what hides the controls) ──
  /**
   * Queue a re-index of one document (id given) or the whole repository.
   * `mode: 'pending'` queues only out-of-date documents; `'full'` (default)
   * re-queues everything — callers must confirm with the user first, showing
   * `reindexPending()`'s counts, because a full run re-embeds the corpus.
   */
  reindex?(documentId?: string, mode?: 'full' | 'pending'): Promise<DmsReindexStatus & { queued: number }>
  /** Progress of the current re-index run; `active` while jobs remain. */
  reindexStatus?(): Promise<DmsReindexStatus>
  /** How much of the repository a re-index would actually change. */
  reindexPending?(): Promise<DmsReindexPending>

  outstandingAcks(): Promise<DmsOutstandingAck[]>
  acknowledge(assignmentId: string): Promise<void>
  feedback(input: {
    rating: 'up' | 'down'
    category?: string
    documentId?: string
    question?: string
    answer?: string
    comment?: string
  }): Promise<void>

  /** The people a USER field can name. Optional: a host without a directory
   *  (the deployed runtime) omits it and the field degrades to a text box. */
  listUsers?(): Promise<DmsUser[]>

  /** Create a folder. Only offered where the structure allows user folders. */
  createFolder?(input: { name: string; parentId?: string }): Promise<{ id: string }>

  /**
   * Rearrange the tree: a folder under a different parent, at a different
   * place among its siblings, or both. Everything filed inside travels with
   * it. `position` is the slot among the destination's children; omitted means
   * last. Optional so a host that does not serve it simply takes no folder
   * drags.
   */
  moveFolder?(input: {
    folderId: string
    parentId?: string
    position?: number
  }): Promise<{ folder_id: string; parent_id: string; position: number }>

  /**
   * How many documents sit in each folder, keyed by folder id (`''` = unfiled).
   * A navigation hint, rolled up the tree; see `repository.folder_counts` for
   * why it is not permission-trimmed.
   */
  folderCounts?(): Promise<Record<string, number>>

  /**
   * Give a document the name people call it by.
   *
   * The title is the ONE name the product shows — list, detail header,
   * citations, audit rows — and it starts life as whatever the uploaded file
   * happened to be called. `Scan_20260812_0003.pdf` is a fact about somebody's
   * scanner, not a document name, so it has to be changeable.
   */
  rename?(documentId: string, title: string): Promise<{ title: string }>

  /** File a document in a different folder. Enforces the destination's
   *  declared types exactly as an upload does. */
  move?(documentId: string, folderId: string): Promise<{ folder_id: string }>

  /**
   * Define a kind of document from the runtime, without opening the designer.
   *
   * Administrator-only on the server, and the type is stamped `runtime`, so a
   * later design seed leaves it exactly as the administrator left it. Optional
   * so a host that does not serve it simply does not offer the option — which
   * is how the deployed runtime hides it.
   */
  createType?(input: {
    key: string
    title: string
    /** `off` | `optional` | `required` — whether documents of this type are
     *  written in the built-in editor rather than uploaded. */
    authoring?: string
    retentionYears?: number
    reviewMonths?: number
    fields?: Array<{ key: string; title: string; data_type?: string
                     mandatory?: boolean; default?: string
                     /** System-filled: always the default, never editable. */
                     protect?: boolean }>
  }): Promise<{ id: string }>

  /** Review feedback. Optional so a host that does not serve it simply hides it. */
  submitReview?(input: {
    documentId: string
    versionId?: string
    summary?: string
    items: Array<{
      kind: 'rewrite' | 'comment'
      suggestion: string
      anchor_text?: string
      page_no?: number
      section_label?: string
      anchor_start?: number
      anchor_end?: number
    }>
  }): Promise<{ review_id: string; items: number }>

  listReviews?(input: { documentId?: string; statuses?: string[] }): Promise<DmsReview[]>

  decideReview?(input: {
    reviewId: string
    accepted?: string[]
    rejected?: string[]
    note?: string
  }): Promise<{ accepted: number; rejected: number; open: number }>

  approveReview?(input: { reviewId: string; comment?: string }): Promise<{ instance_id: string }>

  draftRevision?(input: {
    documentId: string
    versionId?: string
    useAi?: boolean
  }): Promise<{ version_id: string; items: number; method: string }>
}

/**
 * What each lifecycle state is CALLED on screen.
 *
 * "Published" was the one worth changing: it reads like a blog post, when the
 * only thing a reader needs to know is that this is the version they are
 * required to follow. Everything else is the same idea said without the
 * workflow vocabulary — "in review" describes the machine, "waiting for
 * approval" describes what is happening to the document.
 *
 * A repository may declare states this map has never heard of; every consumer
 * falls back to the raw slug, so an unknown state renders as itself rather
 * than disappearing.
 */
export const STATE_LABELS: Record<string, string> = {
  draft: 'Draft',
  review: 'Waiting for approval',
  approved: 'Approved',
  published: 'In force',
  archived: 'Withdrawn',
}

/**
 * A state this map has never heard of, said as well as it can be.
 *
 * A repository may declare its own states, and until now an unknown one
 * rendered as the raw slug — a lowercase `superseded` sitting in a row of
 * properly-cased labels, looking like a bug. De-slugged and sentence-cased it
 * at least reads as a word somebody chose.
 */
/**
 * What a lifecycle action's BUTTON says.
 *
 * A repository declares its transitions by verb — `submit`, `approve`,
 * `reject` — and those are identifiers, not labels. Rendering them raw put
 * lower-case words on the buttons that move a controlled document, beside
 * every other control in the app that is written in sentence case. An
 * author's own `label` always wins; this is only the fallback.
 */
export function actionLabel(action: { action?: string; label?: string }): string {
  const given = String(action?.label || '').trim()
  if (given) return given
  const words = String(action?.action || '').replace(/[_-]+/g, ' ').trim()
  if (!words) return ''
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function humaniseState(state: string): string {
  const known = STATE_LABELS[state]
  if (known) return known
  const words = String(state || '').replace(/[_-]+/g, ' ').trim()
  if (!words) return ''
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * What each state MEANS for the person reading it, in one sentence.
 *
 * A chip alone says `approved`; it does not say whether the reader should be
 * doing what the document says today. That is the question every one of these
 * answers, and it is why the redesign puts a sentence under every status.
 */
export const STATE_MEANING: Record<string, string> = {
  draft: 'Not in force. Only people who can edit it will see it.',
  review: 'Not in force yet. The version already in force still applies.',
  approved: 'Approved, but not yet released to readers.',
  published: 'This is the version everyone must follow.',
  archived: 'No longer in use. Kept as a record.',
}

/**
 * The canonical progression a controlled document makes. Used ONLY to tell a
 * forward transition from a returning one, so a rejection can insist on a
 * reason — a repository is free to declare its own states, and a transition
 * touching one this list does not know is treated as forward (optional
 * comment) rather than guessed at.
 */
export const STATE_ORDER: string[] = [
  'draft', 'review', 'approved', 'published', 'archived',
]

/** True when this action sends the document BACK — the rejection case, where
 *  the reason is the whole payload: it returns to the owner, and a rejection
 *  they cannot act on is a rejection that wasted a cycle. */
export function isReturningAction(action: { from?: string; to?: string }): boolean {
  const from = STATE_ORDER.indexOf(String(action.from || '').toLowerCase())
  const to = STATE_ORDER.indexOf(String(action.to || '').toLowerCase())
  if (from < 0 || to < 0) return false
  return to < from
}

/** Colour per state, as a canonical token. */
export const STATE_TONE: Record<string, string> = {
  draft: 'var(--color-text-secondary)',
  review: 'var(--color-warning)',
  approved: 'var(--color-success)',
  published: 'var(--color-success)',
  archived: 'var(--color-text-secondary)',
}

/**
 * The same thing as a MEANING rather than a colour, for `StatusChip`.
 *
 * `approved` is deliberately not `ok`: something approved but not yet released
 * is not the version to follow, and painting it the same green as one that is
 * in force is exactly the confusion the chip exists to prevent.
 */
export const STATE_CHIP_TONE: Record<string, 'neutral' | 'ok' | 'warn' | 'bad' | 'accent'> = {
  draft: 'neutral',
  review: 'warn',
  approved: 'accent',
  published: 'ok',
  archived: 'neutral',
}

/**
 * The facts about a document that are NOT its lifecycle state, said plainly.
 *
 * `checked_out_by` used to render as "checked out by mwebb" — library jargon
 * plus a login name — and `rights.download === false` rendered as the two words
 * "view only", which named neither the thing you cannot do nor the reason. Both
 * matter to a reader deciding whether they can act, so both get a sentence.
 */
export function documentFacts(row: {
  checked_out_by?: string | null
  rights?: { download?: boolean }
}, userKey?: string,
   /** Resolves a stored user key to a person's name — see `people.ts`. Without
    *  it the key is shown, which is what every screen did before. */
   nameOf?: (key: string) => string,
): Array<{ text: string; tone: 'neutral' | 'warn' | 'accent' }> {
  const facts: Array<{ text: string; tone: 'neutral' | 'warn' | 'accent' }> = []
  if (row.checked_out_by) {
    const who = nameOf ? nameOf(row.checked_out_by) : row.checked_out_by
    facts.push(row.checked_out_by === userKey
      ? { text: 'You are writing the next version', tone: 'accent' }
      : { text: `${who} is writing the next version`, tone: 'accent' })
  }
  if (row.rights && row.rights.download === false) {
    facts.push({ text: 'Read on screen only', tone: 'neutral' })
  }
  return facts
}

/** How the download restriction is explained when there is room for a sentence. */
export const READ_ONLY_EXPLANATION =
  'You can open this and read it here, but not download or print it.'


export function formatBytes(bytes: number): string {
  if (!bytes) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function formatWhen(value: string | null | undefined): string {
  if (!value) return '—'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleString()
}


// ── display vocabulary ────────────────────────────────────────────────────

/** The DERIVED content status of a master. Rendered as an OUTLINE chip, never
 *  the filled one `STATE_LABELS` uses — it is not a lifecycle state and must
 *  not be mistaken for one. */
export const CONTENT_STATUS_LABELS: Record<string, string> = {
  current: 'Up to date',
  stale: 'Out of date',
  incomplete: 'Incomplete',
  broken: 'Broken',
  empty: 'No documents',
}

export const CONTENT_STATUS_TONE: Record<string, string> = {
  current: 'ok',
  stale: 'warn',
  incomplete: 'warn',
  broken: 'bad',
  empty: 'muted',
}

export const BINDING_LABELS: Record<DmsBindingMode, string> = {
  pinned: 'This exact version',
  latest_approved: 'Latest approved version',
  latest: 'Latest version, approved or not',
}

/** Why an author would choose each. Shown beside the control, because this is
 *  the setting people most often get wrong. */
export const BINDING_HELP: Record<DmsBindingMode, string> = {
  pinned:
    'The set keeps showing this version even after the document is revised. '
    + 'You are told when a newer approved version exists.',
  latest_approved:
    'The set follows the document as it is re-approved. A draft revision is '
    + 'never picked up.',
  latest:
    'The set follows every revision immediately, including unapproved drafts. '
    + 'Use only for working documents.',
}

/** What a slot will ask for before it can be signed (Phase 0.1). */
export interface DmsSignatureChallenge {
  slot_id: string
  slot_key: string
  role: string
  meaning: string
  profile: string
  profile_label: string
  /** `none` — the session alone suffices; else what the box must collect. */
  reauth_mode: 'none' | 'password' | 'otp' | 'reconfirm' | 'phrase'
  components_required: number
  per_signature: boolean
  /** False when this installation cannot satisfy the regime at all. */
  available: boolean
  unavailable_reason: string
  /** Whether this slot wants a DRAWN mark. Answered by the slot, so the screen
   *  never decides for itself to collect one that will be dropped, or to omit
   *  one without which the signature is refused. */
  ink?: boolean
  /**
   * WHAT this signature will be about (D10).
   *
   * A digest of exactly what the step shows, read BEFORE the modal is drawn so
   * it is what the signer actually read. It is posted back with the signature,
   * the server recomputes it, and a step that has moved in between is refused
   * with 409 rather than signed — without it, "I signed this" means "I signed
   * whatever that step happened to say when my click arrived".
   *
   * Empty on a document that is not a document form, which is most of them.
   */
  content_token?: string
  step_key?: string
  /** True when an earlier signature on the same step already pinned it, so a
   *  later signer is signing the SAME content rather than today's reading. */
  content_frozen?: boolean
}

export const SIGNATURE_MEANING_LABELS: Record<string, string> = {
  authorship: 'Authored',
  review: 'Reviewed',
  approval: 'Approved',
  responsibility: 'Responsible',
  witness: 'Witnessed',
  seal: 'Sealed',
  // Phase 0.2 — what an EXECUTED record needs. Performing a step and
  // verifying one are different attestations, and neither is approval.
  performance: 'Performed',
  verification: 'Verified',
}

/**
 * What a signer is attesting to, said in full.
 *
 * 21 CFR 11.50(a)(3) requires the MEANING to be recorded *and displayed* — so
 * it has to be shown at the moment of signing, in words, not chosen from a
 * dropdown of nouns. These are the sentences the signing modal shows above the
 * credential box.
 */
export const SIGNATURE_MEANING_STATEMENTS: Record<string, string> = {
  authorship: 'I am the author of this document.',
  review: 'I have reviewed this document.',
  approval: 'I approve this document.',
  responsibility: 'I take responsibility for this document.',
  witness: 'I witnessed this being done.',
  seal: 'I am applying the organisation’s seal.',
  performance: 'I performed this step, and the values recorded are what I observed.',
  verification: 'I verified this step and the values recorded against it.',
}

/**
 * 21 CFR 11.100(c)(1) — the attestation the signer must be shown.
 *
 * Shown verbatim at the moment of signing. It is not decoration and it is not
 * ours to reword: the regulation is about the person understanding what the
 * keystroke is legally equivalent to.
 */
export const SIGNATURE_ATTESTATION =
  'By entering your credentials you are applying your electronic signature, '
  + 'which is the legally binding equivalent of your handwritten signature.'

// ── audit vocabulary ──────────────────────────────────────────────────────
//
// The access log stores an action slug per event. An evidence log read by an
// auditor has to say what happened in words, and has to group by the kind of
// event being evidenced — "show me every approval decision on this document"
// is the question, not "show me every row whose action is `lifecycle`".
//
// Every key below is a slug the server actually writes. An action that is not
// listed still renders (de-slugged) and lands in the `other` module, so a new
// server-side action shows up as an unlabelled row rather than disappearing.

export const AUDIT_ACTION_LABELS: Record<string, string> = {
  upload: 'Filed a document',
  authoring_save: 'Saved a version written here',
  auto_filed: 'Filed into a folder',
  thumbnail: 'Saw its preview in a list',
  renamed: 'Renamed it',
  moved: 'Filed it somewhere else',
  revision_draft: 'Drafted a revision',
  lifecycle: 'Moved it to the next step',
  approval_decided: 'Made an approval decision',
  review_submit: 'Submitted review comments',
  review_decide: 'Accepted or turned down review comments',
  review_approval: 'Sent review comments for approval',
  acknowledged: 'Confirmed they had read it',
  ack_assign: 'Asked people to confirm they had read it',
  review_reminder: 'Sent a reminder to check it again',
  acl_set: 'Changed who can do what',
  flags_set: 'Changed how it can be found',
  doctype_upsert: 'Changed a kind of document',
  field_upsert: 'Changed a detail people fill in',
  field_hidden: 'Hid a detail',
  folder_create: 'Created a folder',
  business_area: 'Changed the business area',
  download: 'Downloaded the file',
  view_page: 'Opened a page',
  metadata_image: 'Opened a metadata image',
  browse: 'Looked through the library',
  qa_query: 'Asked the assistant',
  qa_search: 'Searched inside the documents',
  compare: 'Compared two versions',
  authoring_compare: 'Compared two versions written here',
  reveal: 'Was shown a hidden detail',
  indexed: 'Read so it can be found',
  ai_extract: 'Had its details read out automatically',
  scan_blocked: 'Stopped by the virus check',
  check_out: 'Started writing the next version',
  check_out_cancel: 'Stopped writing the next version',
  metadata_set: 'Changed its details',
  metadata_confirm: 'Confirmed a suggested detail',
  metadata_txn: 'Took its details from a record',
  retention_applied: 'Reached the end of how long it is kept',
  destroyed: 'Destroyed by an administrator',
  delete: 'Deleted a document',
  import_queued: 'Started a bulk import',
  import_errors: 'Bulk import ran into problems',
  sign_apply: 'Signed',
  sign_decline: 'Declined to sign',
  sign_request_open: 'Opened a signing request',
  sign_external_begin: 'Started an external signature',
  evidence_export: 'Exported proof of the signatures',
  save_filter: 'Saved a filter',
  delete_filter: 'Deleted a saved filter',
  compound_add: 'Added a child document',
  compound_remove: 'Removed a child document',
  compound_update: 'Changed a child binding',
  compound_reorder: 'Reordered child documents',
  compound_freeze: 'Froze a baseline',
  compound_merge: 'Combined a set into one file',
  compound_merge_download: 'Downloaded a combined set',
  feedback: 'Said whether an answer was right',
  feedback_closed: 'Closed off that feedback',
}

/** Which module an action is evidence FOR. `access` is the read trail; it is
 *  far noisier than the rest, which is why the evidence log lets an auditor
 *  narrow to the decision trail without it drowning them. */
export const AUDIT_MODULES: Record<string, string[]> = {
  approval: ['lifecycle', 'approval_decided', 'review_submit', 'review_decide',
             'review_approval', 'revision_draft'],
  publication: ['upload', 'authoring_save', 'auto_filed', 'renamed', 'moved',
                'indexed',
                'ai_extract', 'scan_blocked', 'check_out', 'check_out_cancel',
                'metadata_set', 'metadata_confirm', 'metadata_txn',
                'import_queued', 'import_errors'],
  acknowledgement: ['acknowledged', 'ack_assign', 'review_reminder'],
  access: ['browse', 'view_page', 'thumbnail', 'metadata_image', 'download', 'qa_query',
           'qa_search', 'compare', 'authoring_compare', 'reveal', 'feedback',
           'feedback_closed'],
  administration: ['acl_set', 'flags_set', 'doctype_upsert', 'field_upsert',
                   'field_hidden', 'folder_create', 'business_area',
                   'retention_applied', 'destroyed', 'delete',
                   'evidence_export'],
  signature: ['sign_apply', 'sign_decline', 'sign_request_open',
              'sign_external_begin'],
  compound: ['compound_add', 'compound_remove', 'compound_update',
             'compound_reorder', 'compound_freeze', 'compound_merge',
             'compound_merge_download'],
}

export const AUDIT_MODULE_LABELS: Record<string, string> = {
  approval: 'Approvals',
  publication: 'Publication',
  acknowledgement: 'Acknowledgements',
  access: 'Access',
  administration: 'Administration',
  signature: 'Signatures',
  compound: 'Document sets',
  other: 'Other',
}

export function auditActionLabel(action: string): string {
  return AUDIT_ACTION_LABELS[action] || String(action || '').replace(/_/g, ' ')
}

const AUDIT_MODULE_BY_ACTION: Record<string, string> = Object.fromEntries(
  Object.entries(AUDIT_MODULES).flatMap(
    ([module, actions]) => actions.map((action) => [action, module])),
)

export function auditModuleOf(action: string): string {
  return AUDIT_MODULE_BY_ACTION[action] || 'other'
}
